import { CONTRACT_FORM_TABS } from '../components/contracts/ContractFormModal';
import type { ContractResponseDto } from '../services/contracts/ContractService';
import type { ClientResponse } from '../types/dtos';
import type { TeamContractRules } from '../services/teams/teamService';
import {
  cleanPhone,
  isValidCpfLength,
  mergeDddAndLocalPhoneDigits,
} from '../services/utils/stringUtils';
import { evaluateIePolicy } from './iePolicy';
import { lineHasAnyHandset } from './handsetLine';
import { normalizeContractSaleTokenForForm } from './contractClusterTokens';
import { allowsDhDeliverySuspensionWithoutFastchip } from './contractSuspensionEligibility';
import { isFilledDeliveryAddressStreetNumber } from './deliveryAddressStreetNumber';

export type ContractFormTabProgressLine = {
  negociacao?: string;
  seraUsadaPeloCliente?: boolean | null;
  excluirDoContrato?: boolean;
  requerSuspensao?: boolean;
  chipCcid?: string;
  numero?: string;
  ddd?: string;
  aparelho?: string;
  handsetDeviceId?: string;
};

export type ContractFormTabProgressSnapshot = {
  hasClient: boolean;
  client?: IePolicyClientLike | null;
  temSuspensao: boolean | null;
  token: string;
  fastchip: boolean;
  responsibleConsultantUserId: string;
  canAssignResponsibleConsultant: boolean;
  linhas: ContractFormTabProgressLine[];
  meuTimStepRequired: boolean;
  hasCompleteMeuTimCredentials: boolean;
  meuTimVerified: boolean;
  ieBypassConfirmed: boolean;
  cep: string;
  cidade: string;
  estado: string;
  endereco: string;
  numero: string;
  bairro: string;
  adminNomeCompleto: string;
  adminCpf: string;
  adminEmail: string;
  adminTelefone1: string;
  adminTelefone2: string;
  representantes: Array<{ nome?: string; cpf?: string }>;
  teamContractRules: Pick<
    TeamContractRules,
    | 'requireClienteAtivo'
    | 'blockDuplicateCcid'
    | 'blockDuplicatePhone'
    | 'requireSeraUsadaPeloCliente'
  >;
};

type IePolicyClientLike = {
  razaoSocial?: string;
  situacaoCadastral?: string;
  naturezaJuridica?: string | null;
  hasActiveInscricaoEstadual?: boolean | null;
};

function hasDuplicateCcid(linhas: ContractFormTabProgressLine[]): boolean {
  const ccids = linhas
    .filter((linha) => linha.negociacao === 'Aditivo' && linha.chipCcid)
    .map((linha) => linha.chipCcid as string);
  return new Set(ccids).size !== ccids.length;
}

function hasDuplicatePhone(linhas: ContractFormTabProgressLine[]): boolean {
  const phones = linhas
    .filter((linha) => linha.negociacao === 'Renegociação' && linha.numero)
    .map((linha) => {
      const n = cleanPhone(linha.numero ?? '');
      const d = linha.ddd || '';
      return d && n ? mergeDddAndLocalPhoneDigits(d, n) : n;
    });
  return new Set(phones).size !== phones.length;
}

function isClienteCadastralInativo(client?: IePolicyClientLike | null): boolean {
  if (!client?.situacaoCadastral) return false;
  return client.situacaoCadastral.trim().toUpperCase() !== 'ATIVA';
}

function isAdministratorComplete(snapshot: ContractFormTabProgressSnapshot): boolean {
  const telefone1Limpo = snapshot.adminTelefone1.trim();
  const telefone1Digitos = telefone1Limpo.replace(/\D/g, '');
  const telefone2Limpo = snapshot.adminTelefone2.trim();
  const telefone2Digitos = telefone2Limpo.replace(/\D/g, '');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!snapshot.adminNomeCompleto.trim()) return false;
  if (!snapshot.adminCpf.trim() || !isValidCpfLength(snapshot.adminCpf)) return false;
  if (!snapshot.adminEmail.trim() || !emailRegex.test(snapshot.adminEmail.trim())) return false;
  if (!telefone1Limpo) return false;
  if (telefone1Digitos.length !== 10 && telefone1Digitos.length !== 11) return false;
  if (telefone2Limpo && telefone2Digitos.length !== 10 && telefone2Digitos.length !== 11) return false;
  return true;
}

function isRepresentativesComplete(snapshot: ContractFormTabProgressSnapshot): boolean {
  if (!snapshot.representantes.length) return false;
  return snapshot.representantes.every(
    (rep) => !!rep.nome?.trim() && !!rep.cpf && isValidCpfLength(rep.cpf)
  );
}

export function isContractFormTabComplete(
  tabKey: string,
  snapshot: ContractFormTabProgressSnapshot
): boolean {
  switch (tabKey) {
    case 'cliente':
      return snapshot.hasClient;

    case 'contrato': {
      if (snapshot.temSuspensao === null) return false;
      if (!normalizeContractSaleTokenForForm(snapshot.token, snapshot.fastchip)) return false;
      if (snapshot.canAssignResponsibleConsultant && !snapshot.responsibleConsultantUserId.trim()) {
        return false;
      }
      if (
        snapshot.teamContractRules.requireClienteAtivo &&
        isClienteCadastralInativo(snapshot.client) &&
        !snapshot.fastchip
      ) {
        return false;
      }
      if (snapshot.fastchip && snapshot.linhas.some((linha) => lineHasAnyHandset(linha))) {
        return false;
      }
      if (!snapshot.fastchip && !snapshot.ieBypassConfirmed) {
        const policy = evaluateIePolicy(snapshot.client, snapshot.fastchip, snapshot.linhas);
        if (policy !== 'ok') return false;
      }
      return true;
    }

    case 'linhas': {
      if (snapshot.linhas.length === 0) return false;
      if (snapshot.teamContractRules.blockDuplicateCcid && hasDuplicateCcid(snapshot.linhas)) {
        return false;
      }
      if (snapshot.teamContractRules.blockDuplicatePhone && hasDuplicatePhone(snapshot.linhas)) {
        return false;
      }
      if (snapshot.teamContractRules.requireSeraUsadaPeloCliente) {
        const pending = snapshot.linhas.filter(
          (linha) =>
            linha.negociacao === 'Aditivo' &&
            (linha.seraUsadaPeloCliente === null || linha.seraUsadaPeloCliente === undefined)
        );
        if (pending.length > 0) return false;
      }
      return true;
    }

    case 'linhasDesativar': {
      if (snapshot.temSuspensao !== true) return true;
      const suspensionLines = snapshot.linhas.filter(
        (linha) => linha.requerSuspensao === true || linha.excluirDoContrato === true
      );
      return suspensionLines.length > 0;
    }

    case 'meuTim': {
      if (!snapshot.meuTimStepRequired) return true;
      const suspensionLines = snapshot.linhas.filter(
        (linha) => linha.requerSuspensao === true || linha.excluirDoContrato === true
      );
      if (suspensionLines.length === 0) return false;
      if (!snapshot.hasCompleteMeuTimCredentials) return false;
      return snapshot.meuTimVerified;
    }

    case 'endereco':
      return !!(
        snapshot.cep.trim() &&
        snapshot.cidade.trim() &&
        snapshot.estado.trim() &&
        snapshot.endereco.trim() &&
        isFilledDeliveryAddressStreetNumber(snapshot.numero) &&
        snapshot.bairro.trim()
      );

    case 'administrador':
      return isAdministratorComplete(snapshot);

    case 'representantes':
      return isRepresentativesComplete(snapshot);

    case 'recebedores':
    case 'testemunhas':
    case 'observacoesInternas':
      return true;

    default:
      return true;
  }
}

export function resolveFirstIncompleteContractFormTab(
  snapshot: ContractFormTabProgressSnapshot
): string {
  for (const tab of CONTRACT_FORM_TABS) {
    if (!isContractFormTabComplete(tab.key, snapshot)) {
      return tab.key;
    }
  }
  return 'observacoesInternas';
}

export function buildTabProgressSnapshotFromContract(
  contract: ContractResponseDto,
  client: ClientResponse,
  options: {
    hydratedToken: string;
    canAssignResponsibleConsultant: boolean;
    teamContractRules: ContractFormTabProgressSnapshot['teamContractRules'];
    meuTimVerified?: boolean;
  }
): ContractFormTabProgressSnapshot {
  const linhas: ContractFormTabProgressLine[] = (contract.linhas ?? []).map((linha) => ({
    negociacao: linha.negociacao ?? '',
    seraUsadaPeloCliente: linha.seraUsadaPeloCliente ?? null,
    excluirDoContrato: linha.excluirDoContrato ?? false,
    requerSuspensao: linha.requerSuspensao ?? false,
    chipCcid: linha.chipCcid ?? '',
    numero: linha.numero ?? '',
    ddd: linha.ddd ?? '',
    aparelho: linha.aparelho ?? '',
    handsetDeviceId: (linha as { handsetDeviceId?: string }).handsetDeviceId,
  }));

  const suspensionLines = linhas.filter(
    (linha) => linha.requerSuspensao === true || linha.excluirDoContrato === true
  );
  const temSuspensao = suspensionLines.length > 0 ? true : false;
  const fastchip = contract.fastchip ?? false;
  // TEMPORÁRIO: DH por entrega com suspensão — Meu TIM opcional (mesmo critério do modal).
  const dhDeliverySuspensionAllowed = allowsDhDeliverySuspensionWithoutFastchip({
    clusterToken: options.hydratedToken,
    clientEstado: client.estado,
  });
  const meuTimStepRequired = temSuspensao && !fastchip && !dhDeliverySuspensionAllowed;
  const hasMeuTimCredentials = (contract.linhas ?? []).some((linha) => {
    const login = (linha as { meuTimLogin?: string }).meuTimLogin?.trim();
    const senha = (linha as { meuTimSenha?: string }).meuTimSenha?.trim();
    return !!(login && senha);
  });

  return {
    hasClient: true,
    client: {
      razaoSocial: client.razaoSocial,
      situacaoCadastral: client.situacaoCadastral,
      naturezaJuridica: client.naturezaJuridica,
      hasActiveInscricaoEstadual: client.hasActiveInscricaoEstadual,
    },
    temSuspensao,
    token: options.hydratedToken,
    fastchip,
    responsibleConsultantUserId: contract.userId ?? '',
    canAssignResponsibleConsultant: options.canAssignResponsibleConsultant,
    linhas,
    meuTimStepRequired,
    hasCompleteMeuTimCredentials: hasMeuTimCredentials,
    meuTimVerified: options.meuTimVerified ?? false,
    ieBypassConfirmed: contract.ieBypassedAtCreation ?? false,
    cep: contract.deliveryAddress?.cep ?? '',
    cidade: contract.deliveryAddress?.cidade ?? '',
    estado: contract.deliveryAddress?.estado ?? '',
    endereco: contract.deliveryAddress?.endereco ?? '',
    numero: contract.deliveryAddress?.numero ?? '',
    bairro: contract.deliveryAddress?.bairro ?? '',
    adminNomeCompleto: contract.administrator?.nomeCompleto ?? '',
    adminCpf: contract.administrator?.cpf ?? '',
    adminEmail: contract.administrator?.email ?? '',
    adminTelefone1: contract.administrator?.telefone1 ?? '',
    adminTelefone2: contract.administrator?.telefone2 ?? '',
    representantes: (contract.representatives ?? []).map((rep) => ({
      nome: rep.nome,
      cpf: rep.cpf,
    })),
    teamContractRules: options.teamContractRules,
  };
}
